import { useContext, useMemo, useState } from 'react';

import { AppContext } from '../../../core/context/appContextProvider';
import {
  getAllArkAccounts,
  getAllEntities,
  getArkLedgers,
} from '../../../services/arkGL.service';
import { getBankRules, getBanks } from '../../../services/bank.service';
import { getCapitalCalls } from '../../../services/capitalCalls.service';
import { getFundNames } from '../../../services/fund.service';
import { DateTimeFormat } from '../../../utils/helpers/format.helper';
import {
  BankAccount,
  BankRule,
  GlAccountDetails,
  nameIdFundPair,
  NameIdPair,
  nameIdTypePair,
} from '../../../utils/types/bank.type';
import { CapitalCalls } from '../../../utils/types/capitalCalls.type';
import { BANK_STATUS_FILTER_LIST } from '../bankFeeds/BankFeedList.defaults';

export const useBankfilters = (
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const { informationAlert } = useContext(AppContext);

  const [bankAccountList, setBankAccountList] = useState<BankAccount[]>([]);
  const [fundList, setFundList] = useState<NameIdPair[]>([]);
  const [ledgerList, setLedgerList] = useState<nameIdFundPair[]>([]);
  const [CoAList, setCoAList] = useState<GlAccountDetails[]>([]);
  const [memoEntityResponse, setMemoEntityResponse] = useState<
    nameIdTypePair[]
  >([]);
  const [memoEntityList, setMemoEntityList] = useState<any[]>([]);
  const [capitalCallList, setCapitalCallList] = useState<any[]>([]);
  const [bankRulesResponse, setBankRulesResponse] = useState<BankRule[]>([]);

  const fetchAllBankAccounts = async (isCanceled?: () => boolean) => {
    try {
      const allBanksResponse = await getBanks();

      if (isCanceled?.()) return;

      setBankAccountList(allBanksResponse || null);
    } catch (e) {
      informationAlert('Error getting bank accounts list', 'error');
    }
  };

  const fetchAllFundNames = async (isCanceled?: () => boolean) => {
    try {
      const fundsList = await getFundNames();

      if (isCanceled?.()) return;

      setFundList(fundsList);
    } catch (e) {
      informationAlert('Error getting funds list', 'error');
    }
  };

  const fetchArkLedgerNames = async (isCanceled?: () => boolean) => {
    try {
      const ledgersRes = await getArkLedgers();

      if (isCanceled?.()) return;

      setLedgerList(ledgersRes.items);
    } catch (e) {
      informationAlert('Error getting ledger list', 'error');
    }
  };

  const fetchAllCoANames = async (isCanceled?: () => boolean) => {
    try {
      const accountsRes = await getAllArkAccounts();

      if (isCanceled?.()) return;

      setCoAList(accountsRes.items);
    } catch (e) {
      informationAlert('Error getting accounts list', 'error');
    }
  };

  const fetchEntityList = async (isCanceled?: () => boolean) => {
    try {
      const response = await getAllEntities();

      if (isCanceled?.()) return;

      const sortedResponse = response.items.sort(
        (a: { type: string }, b: { type: string }) => {
          if (a.type < b.type) {
            return -1;
          }
          if (a.type > b.type) {
            return 1;
          }
          return 0;
        }
      );

      const entityList: any = [];

      if (sortedResponse.length >= 1) {
        sortedResponse.map((item: any) => {
          if (item.type === 'PortfolioCompany') {
            item.type = 'Portfolio Company';
          }
          if (item.type === 'GeneralEntity') {
            item.type = 'General Entity';
          }

          entityList.push({
            id: item?.id,
            name: `${item?.name} -  ${item?.type}`,
          });
        });
      }

      entityList.push({
        id: 'Null/Undefined',
        name: 'Null/Undefined',
        entityId: undefined,
      });

      setMemoEntityList(entityList);
      setMemoEntityResponse(sortedResponse);
    } catch (e) {
      informationAlert('Error getting memo entities list.', 'error');
    }
  };

  const fetchCapitalCallList = async (isCanceled?: () => boolean) => {
    try {
      const response: CapitalCalls[] = await getCapitalCalls();

      if (isCanceled?.()) return;

      if (response) {
        const list = response
          .filter((c) => c.status === 'PUBLISHED')
          .map((c) => {
            return {
              ...c,
              label: `${c.name} - ${DateTimeFormat.shortDateString(
                c.date as string
              )} - ${c.fundName}`,
            };
          })
          .sort((a, b) => {
            const dateA = a.date ? new Date(a.date).getTime() : 0;
            const dateB = b.date ? new Date(b.date).getTime() : 0;

            return dateB - dateA;
          });

        setCapitalCallList(list);
      }
    } catch (e) {
      informationAlert('Error getting capital call list', 'error');
    }
  };

  const fetchAllBankRules = async (isCanceled?: () => boolean) => {
    try {
      const allBankRulesResponse = await getBankRules();

      if (isCanceled?.()) return;

      setBankRulesResponse(allBankRulesResponse);
    } catch (e) {
      informationAlert('Error getting bank rules', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedBankAccountsList = useMemo(
    () => bankAccountList?.map((item) => item.id),
    [bankAccountList]
  );

  const selectedFundsList = useMemo(
    () => fundList?.map((item) => item.id),
    [fundList]
  );

  const selectedCoAList = useMemo(
    () => CoAList?.map((item) => item.id),
    [CoAList]
  );

  const selectedMemoEntityList = useMemo(
    () => memoEntityList?.map((item: any) => item.id),
    [memoEntityList]
  );

  const selectedStatusList = useMemo(
    () => BANK_STATUS_FILTER_LIST?.map((item) => item.id),
    [BANK_STATUS_FILTER_LIST]
  );

  const fetchAllFilters = async (isCanceled?: () => boolean) => {
    const promises = [
      fetchAllBankAccounts(isCanceled),
      fetchAllFundNames(isCanceled),
      fetchArkLedgerNames(isCanceled),
      fetchAllCoANames(isCanceled),
      fetchEntityList(isCanceled),
      fetchCapitalCallList(isCanceled),
      // fetchAllBankRules(isCanceled),
    ];

    await Promise.all(promises);
  };

  return {
    fetchAllFilters,
    fetchAllBankAccounts,
    bankAccountList,
    fundList,
    ledgerList,
    CoAList,
    memoEntityList,
    memoEntityResponse,
    capitalCallList,

    selectedBankAccountsList,
    selectedFundsList,
    selectedCoAList,
    selectedMemoEntityList,
    selectedStatusList,
  };
};
